/**
 * OverviewPanel — the unified thesis (RD-56 + RD-65).
 *
 * The Matrix Immunity Theorem (RD-56) collapses all ten Neutralization
 * Theorems to four auditable conditions on a governance system embedded in
 * GL(n,ℂ); the Audit view checks whether a given system is eligible for the
 * structural guarantee. Vulnerability Immunity Distillation (RD-65)
 * instantiates the Attack Surface Convergence Theorem to distill an arbitrary
 * generator set down to its greatest immune subset, stripping the A1 violators.
 * Both are two faces of the same theorem; both run on the real @csk/core
 * GL(n,ℂ) realization. Immunity is structural — by construction, as opposed to
 * a runtime filter that scans for attacks.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { GENERATOR_SETS } from "../data/generatorSets.js";
import {
  attackSurface,
  distillToFixedPoint,
} from "../algorithms/distillation.js";
export function OverviewPanel(): React.JSX.Element {
  const sample = GENERATOR_SETS[0];
  const trace = useMemo(
    () => distillToFixedPoint(sample.generators, sample.authoritySubgroup),
    [sample.generators, sample.authoritySubgroup],
  );
  const initialSurface = attackSurface(sample.generators);
  const immuneCount = trace.immune.length;
  const strippedCount = initialSurface - immuneCount;
  return (
    <PanelSurface.PanelShell
      title="The Thesis"
      subtitle="Audit a system's eligibility for immunity, then distill an arbitrary generator set to its immune core — two faces of the Matrix Immunity Theorem over GL(n,ℂ)."
      accentColor={panelTokens.accent}
    >
      <PanelSurface.ThesisCard
        color={panelTokens.accent}
        glowColor="rgba(139,124,242,0.12)"
        quoteSource="RD-56 Matrix Immunity Theorem · RD-65 Vulnerability Immunity Distillation"
        quote={
          <>
            Once a governance system is embedded in <strong>GL(n,ℂ)</strong>,
            the ten Neutralization Theorems collapse to{" "}
            <strong>four auditable conditions</strong> — and any generator set
            can be distilled to its <strong>greatest immune subset</strong>.
            Immunity is structural, by construction, as opposed to a runtime
            filter that scans for attacks.
          </>
        }
      >
        This demo unites the two faces of the{" "}
        <strong className="text-[var(--mit-bright)]">
          Matrix Immunity Theorem
        </strong>{" "}
        (MIT) over <strong className="text-[var(--mit-accent)]">GL(n,ℂ)</strong>
        . The <strong className="text-[var(--mit-bright)]">audit face</strong>{" "}
        (RD-56) collapses all ten Neutralization Theorems to four linear-algebra
        conditions — A1–A4 — on a system embedded in GL(n,ℂ), the terminal
        universal model. The Audit Lab checks whether a <em>given</em> system is{" "}
        <em>eligible</em> for the structural guarantee: when all four conditions
        pass, the ten theorems hold unconditionally. The{" "}
        <strong className="text-[var(--vid-bright)]">distillation face</strong>{" "}
        (RD-65, Vulnerability Immunity Distillation) instantiates the Attack
        Surface Convergence Theorem to take an <em>arbitrary</em> generator set
        and distill it down to its{" "}
        <strong className="text-[var(--vid-accent)]">
          greatest immune subset
        </strong>
        , stripping every generator that violates A1 (the operator-supplied
        authority subgroup) while A2/A3/A4 hold for every gl(n,ℂ) generator by
        construction. Both faces run on the same real{" "}
        <strong className="text-[var(--vid-bright)]">@csk/core GL(n,ℂ)</strong>{" "}
        realization: in either view, the forbidden construction never typechecks
        — immunity is structural by construction, not maintained by a runtime
        guard.
      </PanelSurface.ThesisCard>

      <PanelSurface.SectionHeading>Two faces, one theorem</PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="Eligibility audit vs. distillation to the immune core"
        tone="info"
      >
        The audit asks: <em>does this system satisfy A1–A4?</em> If so it is
        eligible — all ten Neutralization Theorems follow by construction. The
        distillation asks the dual:{" "}
        <em>given any generator set, what is its immune core?</em> Iteratively
        strip the A1 violators (exp(X) ∉ H) until a fixed point remains; that
        fixed point is the greatest immune subset, and assembleCIT then
        certifies all ten theorems on it. Neither face is a runtime scan: the
        forbidden construction is structurally inexpressible.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>What this demo measures</PanelSurface.SectionHeading>
      <PanelSurface.KpiGrid>
        <PanelSurface.KpiBox
          label="Conditions"
          value="4"
          color={panelTokens.accentBlue}
          subtitle="A1 · A2 · A3 · A4"
        />
        <PanelSurface.KpiBox
          label="NTs collapsed"
          value="10"
          color={panelTokens.accentPurple}
          subtitle="ten → four"
        />
        <PanelSurface.KpiBox
          label="Universal model"
          value="GL(n,ℂ)"
          color={panelTokens.carrier}
          subtitle="terminal · faithful embedding"
        />
        <PanelSurface.KpiBox
          label="Violable axiom"
          value="1"
          color={panelTokens.distill}
          subtitle="A1 authority subgroup H"
        />
        <PanelSurface.KpiBox
          label="Structural over ℂ"
          value="3"
          color={panelTokens.immune}
          subtitle="A2 · A3 · A4 by construction"
        />
        <PanelSurface.KpiBox
          label="Postulates"
          value="0"
          color={panelTokens.immuneGreen}
          subtitle="abstract VID · proved sub-theorems"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.SectionHeading>A worked distillation (live)</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="mx-1 mt-0 mb-0">
          On the @csk/core GL(n,ℂ) realization, the preset{" "}
          <strong className="text-[var(--vid-bright)]">“{sample.title}”</strong>{" "}
          starts with {String(initialSurface)} generators. Distillation strips{" "}
          {String(strippedCount)} A1 violator
          {strippedCount === 1 ? "" : "s"} (exp(X) ∉ H) in {String(trace.steps)}{" "}
          step{trace.steps === 1 ? "" : "s"}, leaving {String(immuneCount)}{" "}
          immune generator
          {immuneCount === 1 ? "" : "s"} — μ(ALLOW₀) = {String(initialSurface)}{" "}
          → μ(IMMUNE) = {String(immuneCount)}. Open the Distillation Lab to run
          it step by step, or the Matrix Audit Lab to audit a single matrix
          against A1–A4.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>The four conditions, in one block</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`A1  Authority subgroup closure   exp(X) ∈ H ⊆ GL(n,ℂ)   → operator-supplied · the sole distillation target
A2  Spectral separation          X = PDP⁻¹ over ℂ        → structural · eigendecomposition always exists
A3  Temporal flow                exp semigroup law        → structural · GL(n,ℂ) carrier
A4  Resource master identity     det(exp X) = exp(Tr X)   → structural · Jacobi identity

-- audit:  all four hold        ⇒ system eligible ⇒ ten NTs hold unconditionally
-- distill: strip A1 violators  ⇒ greatest immune subset ⇒ assembleCIT certifies ten NTs`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>How to read the rest of the demo</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="Matrix Immunity & Distillation reading order"
          variant="ordered"
          items={[
            {
              id: "conditions",
              title: "The Four Conditions (A1–A4)",
              body: "Each condition, the matrix operation it names, the generator stratum it governs, and the NTs it neutralizes.",
              color: panelTokens.accentBlue,
            },
            {
              id: "audit",
              title: "Matrix Audit Lab",
              body: "Pick or edit a GL(n,ℂ) matrix and run the four audits live: each pass/fail with the computed trace, det, eigenvalues, and exp/log residual.",
              color: panelTokens.accentYellow,
            },
            {
              id: "collapse",
              title: "Ten → Four Collapse",
              body: "How A1–A4 imply the ten Neutralization Theorems, with the CDS and FACT corollaries.",
              color: panelTokens.accentCyan,
            },
            {
              id: "axioms",
              title: "MIT Axioms (distillation view)",
              body: "The same four axioms as the violable governance choice (A1, the authority subgroup H) versus the three structural axioms that hold for every gl(n,ℂ) generator.",
              color: panelTokens.carrier,
            },
            {
              id: "lab",
              title: "Distillation Lab",
              body: "Pick a generator set, strip the A1 violators step by step, and watch the attack-surface count μ descend monotonically to the immune fixed point.",
              color: panelTokens.distill,
            },
            {
              id: "convergence",
              title: "Convergence & Containment",
              body: "Why the iteration terminates (well-founded descent on μ) and why the result is the greatest immune subset contained in the initial set (the RD-65 containment lemma).",
              color: panelTokens.immune,
            },
            {
              id: "foundation",
              title: "Foundation & Mechanization",
              body: "The shared @csk/core GL(n,ℂ) realization both faces run on, plus the honest mechanization status for RD-56 and RD-65.",
              color: panelTokens.accentTeal,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-56 Matrix Immunity Theorem",
            path: "research/settled/RD-56-MATRIX-IMMUNITY-THEOREM.md",
          },
          {
            label: "RD-65 Vulnerability Immunity Distillation",
            path: "research/settled/RD-65-IMMUNITY-DISTILLATION.md",
          },
          {
            label: "GL(2,ℂ) realization (@csk/core, RD-98)",
            path: "realizations/typescript/src/research/gl2c-tangent-embedding.ts",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
